/**
 * THE easing module (M8). One source of truth for every render-layer curve.
 *
 * M8 bans linear tweens in render-layer motion and requires every curve to
 * come from here. A second easing implementation elsewhere is a bug, not a
 * style choice — the reason is that "the game feels consistent" is a property
 * of curves agreeing with each other, and two implementations drift the
 * moment one is tweaked.
 *
 * RENDER LAYER ONLY. These use transcendentals, which are banned in sim code
 * (Iron Rule 1, AUDIT A-03) because they are not bit-identical across JS
 * engines. Nothing here may ever be imported by `packages/run-engine`.
 *
 * Every function maps [0,1] -> [0,1] except `settle`, which deliberately
 * overshoots above 1 before returning — that overshoot is the point.
 */

/** Slow to fast. Anticipation: the wind-up before a blow. */
export const inCubic = (t: number): number => t * t * t;

/** Fast to slow. The default for anything arriving somewhere. */
export const outCubic = (t: number): number => 1 - (1 - t) ** 3;

/** Slow, fast, slow. The default for anything that starts and stops. */
export const inOutCubic = (t: number): number =>
  (t < 0.5 ? 4 * t * t * t : 1 - (-2 * t + 2) ** 3 / 2);

/** Very slow to very fast — a whip. For an impact frame. */
export const inQuint = (t: number): number => t ** 5;

/**
 * Overshoot and settle, like weight arriving on a joint.
 *
 * A recoil that returns exactly to rest reads as mechanical: real mass
 * overshoots and comes back. `damp` is how quickly the oscillation dies;
 * higher is tighter. Returns values slightly above 1 near the start of the
 * return, which is what makes the settle visible.
 */
export const settle = (t: number, damp = 6, freq = 2.2): number => {
  if (t <= 0) return 0;
  if (t >= 1) return 1;
  return 1 - Math.exp(-damp * t) * Math.cos(freq * Math.PI * t);
};

/**
 * Piecewise time remap for a three-phase action (M3's fire law).
 *
 * Splits normalised time into anticipation / impact / recovery and gives each
 * its own curve, so one clip reads as three beats rather than one smear:
 * the wind-up eases IN (slow, gathering), the impact is a whip (fast), and
 * the recovery settles. `a` and `b` are the phase boundaries in output time.
 */
export const threePhase = (t: number, a = 0.35, b = 0.5): number => {
  if (t < a) return inCubic(t / a) * a;
  if (t < b) return a + inQuint((t - a) / (b - a)) * (b - a);
  return b + outCubic((t - b) / (1 - b)) * (1 - b);
};

/** Triangle wave on [0,1] — one up-down cycle. For a stride bob. */
export const bob = (t: number): number => Math.sin(t * Math.PI * 2);
